import React, { useRef } from 'react';
import {
  Typography,
  Box,
  Container,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Chip
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { shades } from '../../../theme';
import doctorImage from '../../data/home/doctor.jpg';
import pharmImage from '../../data/home/pharm.jpg';
import hospitalImage from '../../data/home/hospital.avif';
import learnImage from '../../data/home/learn.jpg';
import finImage from '../../data/home/finance.avif';
import communityImage from '../../data/home/community.jpg';

const FeaturedServices = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');

  const featuredServices = [
    {
      id: 1,
      name: 'Dentistry',
      description: 'Expert dental care for all your oral health needs',
      image: doctorImage,
      rating: 4.9,
      specialists: 48
    },
    {
      id: 2,
      name: 'Therapy',
      description: 'Professional mental health counseling and support',
      image: pharmImage,
      rating: 4.8,
      specialists: 52
    },
    {
      id: 3,
      name: 'Pediatrics',
      description: 'Specialized healthcare for infants, children and adolescents',
      image: hospitalImage,
      rating: 4.9,
      specialists: 37
    },
    {
      id: 4,
      name: 'Cardiology',
      description: 'Comprehensive heart care and cardiovascular treatments',
      image: finImage,
      rating: 4.7,
      specialists: 29
    },
    {
      id: 5,
      name: 'Dermatology',
      description: 'Expert skin, hair, and nail treatments',
      image: communityImage,
      rating: 4.6,
      specialists: 31
    },
    {
      id: 6,
      name: 'Orthopedics',
      description: 'Specialized care for bones, joints, and muscles',
      image: learnImage,
      rating: 4.8,
      specialists: 25
    },
    {
      id: 7,
      name: 'Obstetrics',
      description: 'Complete pregnancy and childbirth care',
      image: doctorImage,
      rating: 4.9,
      specialists: 33
    },
    {
      id: 8,
      name: 'Neurology',
      description: 'Specialized care for disorders of the nervous system',
      image: pharmImage,
      rating: 4.7,
      specialists: 22
    },
    {
      id: 9,
      name: 'Ophthalmology',
      description: 'Comprehensive eye care and vision treatments',
      image: hospitalImage,
      rating: 4.8,
      specialists: 19
    }
  ];

  const scrollRef = useRef(null);

  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const scrollAmount = direction === 'left' ? -300 : 300;
      // Get current scroll position
      const startPosition = container.scrollLeft;
      const targetPosition = startPosition + scrollAmount;
      container.scrollTo({
        left: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Container maxWidth='xl' sx={{ py: 5, mt: 2, position: 'relative' }}>
      <Box sx={{
        display: 'flex',
        flexDirection: isTablet ? 'column' : 'row',
        alignItems: isTablet ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        mb: 4
      }}
      >
        <Box sx={{ mb: isTablet ? 2 : 0 }}>
          <Chip
            label='Specialized Care'
            sx={{
              fontWeight: 600,
              backgroundColor: isDarkMode ? shades.neutral[800] : shades.neutral[200],
              color: isDarkMode ? shades.white[100] : shades.secondary[500],
              borderRadius: '16px',
              mb: 2,
              '& .MuiChip-label': {
                px: 2,
                py: 0.5
              }
            }}
          />
          <Typography
            variant={isMobile ? 'h4' : 'h6'}
            sx={{
              fontWeight: 700,
              color: isDarkMode ? shades.white[100] : shades.secondary[500],
              mb: 1
            }}
          >
            Featured Medical Services
          </Typography>
          <Typography
            variant='body1'
            sx={{
              color: isDarkMode ? shades.neutral[400] : shades.secondary[400],
              maxWidth: '600px'
            }}
          >
            Discover our premium healthcare specialties with top-rated professionals
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          gap: 1,
          // backgroundColor: isDarkMode ? shades.neutral[800] : shades.neutral[200],
          borderRadius: 2,
          p: 1
        }}
        >
          <IconButton
            onClick={() => handleScroll('left')}
            sx={{
              color: isDarkMode ? shades.white[100] : shades.secondary[500],
              '&:hover': {
                backgroundColor: isDarkMode ? shades.neutral[700] : shades.neutral[300]
              }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={() => handleScroll('right')}
            sx={{
              color: isDarkMode ? shades.white[100] : shades.secondary[500],
              '&:hover': {
                backgroundColor: isDarkMode ? shades.neutral[700] : shades.neutral[300]
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: 3,
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          pb: 2,
          pt: 1
        }}
      >
        {featuredServices.map((service) => (
          <Card
            key={service.id}
            sx={{
              flex: '0 0 auto',
              width: isMobile ? 260 : 300,
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: isDarkMode
                ? '0 8px 24px rgba(0,0,0,0.2)'
                : '0 8px 24px rgba(0,0,0,0.08)',
              backgroundColor: isDarkMode ? shades.neutral[900] : shades.white[100],
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: isDarkMode
                  ? '0 12px 32px rgba(0,0,0,0.3)'
                  : '0 12px 32px rgba(0,0,0,0.15)'
              }
            }}
          >
            <CardActionArea>
              <CardMedia
                component='img'
                height='160'
                image={service.image}
                alt={service.name}
                sx={{
                  objectFit: 'cover'
                }}
              />
              <CardContent sx={{ p: 3 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1
                }}
                >
                  <Typography
                    variant='h6'
                    sx={{
                        fontWeight: 700,
                        color: isDarkMode ? shades.white[100] : shades.secondary[500]
                      }}
                  >
                    {service.name}
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: isDarkMode ? shades.neutral[800] : shades.neutral[200],
                    borderRadius: 1,
                    px: 1,
                    py: 0.5
                  }}
                  >
                    <Typography
                        variant='body2' sx={{
                            fontWeight: 600,
                            color: isDarkMode ? shades.neutral[300] : shades.secondary[500]
                          }}
                      >
                        {service.rating}
                      </Typography>
                    <Box
                        component='span'
                        sx={{
                            ml: 0.5,
                            color: theme.palette.warning.main,
                            fontSize: '1rem'
                          }}
                      >
                                          ★
                      </Box>
                  </Box>
                </Box>
                <Typography
                  variant='body2'
                  sx={{
                    color: isDarkMode ? shades.neutral[400] : shades.secondary[400],
                    mb: 2,
                    height: '40px',
                    overflow: 'hidden'
                  }}
                >
                  {service.description}
                </Typography>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pt: 1,
                  borderTop: `1px solid ${isDarkMode ? shades.neutral[800] : shades.neutral[200]}`
                }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                        color: isDarkMode ? shades.neutral[400] : shades.secondary[400],
                        fontWeight: 500
                      }}
                  >
                    {service.specialists} specialists
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                        color: isDarkMode ? shades.primary[400] : shades.primary[600],
                        fontWeight: 600
                      }}
                  >
                                      View details →
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      {/* <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 5
                }}
            >
                <Box
                    component="button"
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "14px 32px",
                        fontSize: isMobile ? "16px" : "18px",
                        fontWeight: 600,
                        backgroundColor: "transparent",
                        color: isDarkMode ? shades.white[100] : shades.secondary[500],
                        border: `2px solid ${isDarkMode ? shades.neutral[700] : shades.neutral[300]}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        "&:hover": {
                            backgroundColor: isDarkMode ? shades.neutral[800] : shades.neutral[200],
                            transform: 'translateY(-2px)'
                        },
                    }}
                >
                    Explore all medical specialties
                </Box>
            </Box> */}
    </Container>
  );
};

export default FeaturedServices;
